import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Defaults } from 'app/lib/constants';
import { errorToI18n } from 'app/lib/error';
import { MappedSnowflakeStatusPropType } from 'app/lib/proptypes';
import { navigateToAdmin } from 'app/routes';
import { Banner, BannerVariant } from 'app/components/core/Banner';
import { Callout, ModalBody, ModalHeader } from 'app/components/core/Modal';
import { DeploymentOverviewButton } from 'app/components/features/DeploymentOverviewButton';
import { WarehouseLoadDialogMenuButton } from 'app/components/features/WarehouseLoadDialog';
import { SnowflakeUnconfiguredConnection } from './SnowflakeUnconfiguredConnection.jsx';
import { SnowflakeUserInfo } from './SnowflakeUserInfo.jsx';
import { UsernamePasswordForm } from './UsernamePasswordForm.jsx';
import * as styles from './SnowflakeConnectionModal.css';

function StatusConnectedBody({ account, isWarning, user, onClose }) {
    const { t } = useTranslation();

    return (
        <Fragment>
            <ModalHeader hideCloseButton>
                <div className={styles.modalHeader}>
                    <SnowflakeUserInfo user={user} account={account} />
                </div>
            </ModalHeader>
            {isWarning && (
                <Banner className={styles.banner} variant={BannerVariant.WARNING}>
                    {t(`components:SnowflakeConnectionStatus.connectedWithWarning`)}
                </Banner>
            )}
            <div className={styles.button}>
                <WarehouseLoadDialogMenuButton onClick={onClose} />
            </div>
            <div className={styles.separator} />
        </Fragment>
    );
}

function StatusSignInRequiredBody() {
    return (
        <Fragment>
            <ModalHeader hideCloseButton>
                <SnowflakeUserInfo />
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
                <UsernamePasswordForm />
            </ModalBody>
            <div className={styles.separator} />
        </Fragment>
    );
}

function StatusUnconfiguredBody({ isUnconfiguredAdmin, onClick }) {
    return (
        <Fragment>
            <ModalHeader hideCloseButton>
                <SnowflakeUserInfo />
            </ModalHeader>
            <ModalBody className={styles.modalBody}>
                <SnowflakeUnconfiguredConnection isAdmin={isUnconfiguredAdmin} onClick={onClick} />
            </ModalBody>
            <div className={styles.separator} />
        </Fragment>
    );
}

function StatusErrorBody({ reason }) {
    const { t } = useTranslation();

    const errorTranslation =
        reason && errorToI18n('components:SnowflakeConnectionStatus.error')(reason);

    return (
        <Fragment>
            <ModalHeader hideCloseButton>
                <div className={styles.modalHeader}>
                    <SnowflakeUserInfo />
                </div>
            </ModalHeader>
            <Banner className={styles.banner} variant={BannerVariant.ERROR}>
                {reason
                    ? t(errorTranslation.key, errorTranslation.data)
                    : t('components:SnowflakeConnectionStatus.error.NoHealthcheckReceived')}
            </Banner>
        </Fragment>
    );
}

export function SnowflakeConnectionModal({
    attachTo,
    onClose = Defaults.NOOP,
    account = null,
    mappedSnowflakeStatus = Defaults.OBJECT,
    user = null,
    reason = null
}) {
    const {
        isError,
        isConnectedWithWarning,
        isConnected,
        isUnconfigured,
        isUnconfiguredAdmin,
        isSignInRequired
    } = mappedSnowflakeStatus;

    const goToAdminPage = () => {
        navigateToAdmin();
        onClose();
    };

    return (
        <Callout attachTo={attachTo} onClose={onClose} placement="bottom" className={styles.modal}>
            {isConnected && (
                <StatusConnectedBody
                    account={account}
                    isWarning={isConnectedWithWarning}
                    user={user}
                    onClose={onClose}
                />
            )}
            {isSignInRequired && <StatusSignInRequiredBody />}

            {isUnconfigured && (
                <StatusUnconfiguredBody
                    isUnconfiguredAdmin={isUnconfiguredAdmin}
                    onClick={goToAdminPage}
                />
            )}

            {isError && <StatusErrorBody reason={reason} />}

            <div className={styles.button}>
                <DeploymentOverviewButton onClick={onClose} />
            </div>
        </Callout>
    );
}

SnowflakeConnectionModal.propTypes = {
    attachTo: PropTypes.string.isRequired,
    onClose: PropTypes.func,
    account: PropTypes.string,
    mappedSnowflakeStatus: MappedSnowflakeStatusPropType,
    user: PropTypes.string,
    reason: PropTypes.shape({
        type: PropTypes.string,
        message: PropTypes.string
    })
};
